import { useCallback, useMemo, useState } from 'react';

import type { TabContentConfig, TabItem } from '@types';

interface UseTabsConfig<T extends string = string> {
  defaultTab?: T;
  onTabChange?: (tabKey: T) => void;
  tabs: TabItem[];
  contentConfig?: TabContentConfig<T>;
}

interface UseTabsReturn<T extends string = string> {
  activeTab: T;
  setActiveTab: (tabKey: T) => void;
  handleTabChange: (tabKey: T) => void;
  isActiveTab: (tabKey: T) => boolean;
  getTabIndex: (tabKey: T) => number;
  renderContent: () => React.ReactNode;
  activeTabData: TabItem | undefined;
}

export const useTabs = <T extends string = string>({
  defaultTab,
  onTabChange,
  tabs,
  contentConfig,
}: UseTabsConfig<T>): UseTabsReturn<T> => {
  const [activeTab, setActiveTab] = useState<T>(defaultTab ?? (tabs[0]?.key as T) ?? ('' as T));

  const handleTabChange = useCallback(
    (tabKey: T) => {
      setActiveTab(tabKey);
      onTabChange?.(tabKey);
    },
    [onTabChange],
  );

  const isActiveTab = useCallback(
    (tabKey: T) => {
      return activeTab === tabKey;
    },
    [activeTab],
  );

  const getTabIndex = useCallback(
    (tabKey: T) => {
      return tabs.findIndex((tab) => tab.key === tabKey);
    },
    [tabs],
  );

  const activeTabData = useMemo(() => tabs.find((tab) => tab.key === activeTab), [tabs, activeTab]);

  const renderContent = useCallback(() => {
    if (!contentConfig) return null;

    const contentRenderer = contentConfig[activeTab];
    if (typeof contentRenderer === 'function') {
      return contentRenderer({ activeTab, activeTabData });
    }
    return contentRenderer || null;
  }, [activeTab, activeTabData, contentConfig]);

  return {
    activeTab,
    setActiveTab,
    handleTabChange,
    isActiveTab,
    getTabIndex,
    renderContent,
    activeTabData,
  };
};
